/* eslint-disable no-underscore-dangle */

import {
  AGS_HOST_URL,
  DEFAULT_LOCALE,
  DEFAULT_SITE_CODE,
  DEFAULT_SITE_ID,
  SITE_ID_URL_PARAM_KEY,
  SPECTRABASE_HOST_URL,
  VALID_LOCALES,
  VET_HOST_URL,
  WILEYCOM_HOST_URL,
} from '@config/constants';
import { getSiteConfig } from '@requests/siteConfig';
import {
  SiteConfigResponse,
  SiteConfiguration,
} from '@requests/siteConfig/types';
import { SiteId } from 'wly-ecommerce-common';
import { logError, logWarning } from './log';
import { getSearchParamFromUrl } from './url';

const SUBSCRIPTION_SITE_IDS = [
  SiteId.COD,
  SiteId.AGS,
  SiteId.VET,
  SiteId.SPECTRABASE,
  SiteId.AIGATEWAY,
];

export const APP_MODE = process.env.REACT_APP_APP_MODE as string;
export const COOKIE_DOMAIN = '.wiley.com';
export const AGS_COOKIE_DOMAIN = '.graphicstandards.com';
export const SPECTRABASE_COOKIE_DOMAIN = APP_MODE === 'production' ? 'spectrabase.com' : '.spectrabase.com';

const siteIdToContinueShoppingPathMap: Readonly<Record<SiteId, string>> = {
  [SiteId.WILEYCOM]: '',
  [SiteId.COD]: '/shop/on-demand/chemistry',
  [SiteId.VET]: '/subscription',
  [SiteId.AGS]: '/product-details',
  [SiteId.WILA]: '',
  [SiteId.AUTHOR]: '',
  [SiteId.SPECTRABASE]: '',
  [SiteId.AIGATEWAY]: '',
};

const siteIdToContinueShoppingHostMap: Readonly<Partial<Record<SiteId, string>>> = {
  [SiteId.WILEYCOM]: WILEYCOM_HOST_URL,
  [SiteId.COD]: WILEYCOM_HOST_URL,
  [SiteId.WILA]: WILEYCOM_HOST_URL,
  [SiteId.AUTHOR]: WILEYCOM_HOST_URL,
  [SiteId.VET]: VET_HOST_URL,
  [SiteId.AGS]: AGS_HOST_URL,
  [SiteId.SPECTRABASE]: SPECTRABASE_HOST_URL,
};

export class SiteConfig {
  private static instance: SiteConfig | null = null;

  private readonly _siteId: SiteId;

  private readonly _hostUrl: string;

  private readonly _locale: string;

  private readonly _config?: SiteConfiguration;

  private constructor(
    hostUrl?: string,
    locale?: string,
    siteConfig?: SiteConfigResponse,
  ) {
    if (!hostUrl) {
      const { protocol, hostname } = window.location;
      hostUrl = `${protocol}//${hostname}`;
    }

    this._hostUrl = hostUrl;

    const localeFromUrl = window.location.pathname.slice(1, 6) as string;
    const validLocale = VALID_LOCALES.includes(localeFromUrl)
      ? localeFromUrl
      : DEFAULT_LOCALE;
    this._locale = locale || validLocale;

    if (siteConfig && Object.keys(siteConfig).length === 1) {
      this._siteId = Object.keys(siteConfig)[0] as SiteId;
    } else {
      this._siteId = DEFAULT_SITE_ID;

      const siteIdParam = getSearchParamFromUrl(SITE_ID_URL_PARAM_KEY);

      if (
        siteIdParam
        && Object.values(SiteId).includes(siteIdParam as SiteId)
      ) {
        this._siteId = siteIdParam as SiteId;
      }
    }

    this._config = siteConfig?.[this._siteId];

    Object.freeze(this);
  }

  public static async init(hostUrl?: string, locale?: string): Promise<void> {
    if (this.instance) {
      logWarning('SiteConfig: Already initialized.');
      return;
    }

    const siteConfig = await getSiteConfig(hostUrl);

    if (!siteConfig) {
      logWarning(
        'SiteConfig: Failed to fetch site config, using default values.',
      );
      this.instance = new SiteConfig(hostUrl, locale);
      return;
    }

    this.instance = new SiteConfig(hostUrl, locale, siteConfig);
  }

  public static getConfig(): SiteConfig {
    if (!this.instance) {
      logError('SiteConfig: Not initialized. Call init() first.');
    }

    return this.instance!;
  }

  public get siteId(): SiteId {
    return this._siteId;
  }

  public get hostUrl(): string {
    return this._hostUrl;
  }

  public get locale(): string {
    return this._locale;
  }

  public get lang(): string {
    return this._locale.split('-')[0];
  }

  public get iso(): string {
    return this._locale.split('-')[1];
  }

  public get config(): SiteConfiguration | undefined {
    return this._config;
  }

  public get isSubscriptionSite(): boolean {
    return SUBSCRIPTION_SITE_IDS.includes(this._siteId);
  }

  public get cookieDomain(): string {
    if (this._siteId === SiteId.AGS) {
      return AGS_COOKIE_DOMAIN;
    }

    if (this._siteId === SiteId.SPECTRABASE) {
      return SPECTRABASE_COOKIE_DOMAIN;
    }

    return COOKIE_DOMAIN;
  }

  public get isWileyCom(): boolean {
    return this._siteId === SiteId.WILEYCOM;
  }

  public get isWila(): boolean {
    return this._siteId === SiteId.WILA;
  }

  public get isAuthor(): boolean {
    return this._siteId === SiteId.AUTHOR;
  }

  public get isSpectrabase(): boolean {
    return this._siteId === SiteId.SPECTRABASE;
  }

  public get isAigateway(): boolean {
    return this._siteId === SiteId.AIGATEWAY;
  }

  public get isWileyComHosted(): boolean {
    return [SiteId.COD, SiteId.WILEYCOM, SiteId.WILA, SiteId.AUTHOR].includes(
      this._siteId,
    );
  }

  public get pricingDeterminationModel(): string | undefined {
    return this._config?.pricingDeterminationModel;
  }

  public get siteCode(): string {
    return this._config?.source ?? DEFAULT_SITE_CODE;
  }

  public static buildContinueShoppingUrl(): string {
    const {
      siteId, locale, hostUrl, isWileyComHosted, isAigateway,
    } = this.getConfig();

    const continueShoppingHostUrl = siteIdToContinueShoppingHostMap[siteId] || hostUrl;
    const localeStr = (isWileyComHosted || isAigateway) ? `/${locale}` : '';
    const path = siteIdToContinueShoppingPathMap[siteId];

    return `${continueShoppingHostUrl}${localeStr}${path}`;
  }

  /** @internal Used for testing only */
  public static reset(): void {
    this.instance = null;
  }
}
